import {
  Children,
  type ReactElement,
  type ReactNode,
  cloneElement,
  isValidElement,
} from 'react';

const hasChildren = (
  element: ReactNode
): element is ReactElement<{
  children: ReactNode | ReactNode[];
}> =>
  isValidElement<{ children?: ReactNode[] }>(element) &&
  Boolean(element.props.children);

const hasComplexChildren = (
  element: ReactNode
): element is ReactElement<{
  children: ReactNode | ReactNode[];
}> =>
  isValidElement(element) &&
  hasChildren(element) &&
  Children.toArray(element.props.children).reduce(
    (response: boolean, child: ReactNode): boolean =>
      response || isValidElement(child),
    false
  );

export function deepMapChildren(
  children: ReactNode | ReactNode[],
  deepMapFn: (
    child: ReactNode,
    index?: number,
    children?: ReactNode[]
  ) => ReactNode
): ReactNode[] {
  return Children.toArray(children).map(
    (child: ReactNode, index: number, mapChildren: ReactNode[]) => {
      if (isValidElement(child) && hasComplexChildren(child)) {
        // Clone the child that has children and map them too
        return deepMapFn(
          cloneElement(child, {
            ...child.props,
            children: deepMapChildren(child.props.children, deepMapFn),
          })
        );
      }
      return deepMapFn(child, index, mapChildren);
    }
  );
}
